import * as poiState from '@/components/Analysis/modules/pointOfInterest/state';
import { inspectionMetadata } from '@/components/Analysis/state';
import { useCallback, useEffect, useMemo } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { PointOfInterestTemplate } from '@/__generated__/graphql';

interface DefectData {
  id?: string;
  key: string;
  value: string;
}

const fieldMap = {
  substrate_condition: 'corrosion_category',
  coating_condition: 'coating_condition',
  abyss_confidence: 'abyss_confidence',
  distance_from_scanner: 'distance_from_scanner',
};

const resetStates = new Set(['Verify', 'Update-Template']);

export const usePoiDefectData = () => {
  const structure = useAtomValue(inspectionMetadata);
  const blisterToAdd = useAtomValue(poiState.blisterToAdd);
  const editPointOfInterest = useAtomValue(poiState.editPointOfInterest);
  const setDefectData = useSetAtom(poiState.defectsList);

  const defectLegends = structure?.defectLegends;

  useEffect(() => {
    if (resetStates.has(editPointOfInterest.formState ?? '')) {
      setDefectData([]);
    }
  }, [editPointOfInterest.formState, setDefectData]);

  const setPoiDefectData = useCallback(
    (defectsAttributes: Map<string, number | number[]>) => {
      if (
        (blisterToAdd && blisterToAdd.state === 'WaitingForCenter') ||
        (!blisterToAdd && editPointOfInterest.state === 'WaitingForPoint')
      ) {
        if (!defectLegends?.length) return;

        const updatedDefects: DefectData[] = [];

        defectsAttributes.forEach((value, key) => {
          const normalizedKey = key.toLowerCase();
          const matchingLegends = defectLegends.filter(
            (legend) => legend.octreeKey?.toLowerCase() === normalizedKey
          );

          if (matchingLegends.length > 0) {
            const values = Array.isArray(value) ? value : [value];

            values.forEach((octreeValue) => {
              const legend = matchingLegends.find((l) => l.octreeValue === octreeValue);

              if (legend) {
                updatedDefects.push({
                  key:
                    legend.groupKey in fieldMap
                      ? fieldMap[legend.groupKey as keyof typeof fieldMap]
                      : legend.groupKey,
                  value: legend.name,
                });
              }
            });
          }
        });
        setDefectData(updatedDefects);
      }
    },
    [blisterToAdd, defectLegends, editPointOfInterest.state, setDefectData]
  );

  const getDefectLegendOctreeKeys = useCallback(() => {
    if (!defectLegends?.length) return [];
    const uniqueKeys = new Set<string>();
    defectLegends.forEach((defectLegend) => {
      if (defectLegend.octreeKey) {
        uniqueKeys.add(defectLegend.octreeKey.toLowerCase());
      }
    });

    return Array.from(uniqueKeys);
  }, [defectLegends]);

  const defectLegendKeys = useMemo(() => {
    if (!defectLegends || !defectLegends.length) return [];
    const uniqueKeys = new Set<string>();
    defectLegends.forEach((defectLegend) => {
      if (defectLegend.groupKey) {
        uniqueKeys.add(defectLegend.groupKey.toLowerCase());
      }
    });

    return Array.from(uniqueKeys);
  }, [defectLegends]);

  const switchTemplate = useCallback(
    (newTemplate: PointOfInterestTemplate) => {
      const templateFieldKeys = new Set([
        ...Object.values(fieldMap),
        ...(defectLegends?.map((l) => l.groupKey) ?? []),
      ]);
      const currentFields = editPointOfInterest.template?.fields;
      const currentData = editPointOfInterest.pointOfInterest?.templateFieldData;

      if (!currentFields || !currentData) {
        return [];
      }

      const matchingDefects: DefectData[] = [];

      currentFields.forEach((field) => {
        if (templateFieldKeys.has(field?.key ?? '')) {
          const value = currentData[field.key ?? ''];

          if (value) {
            matchingDefects.push({
              id: field?.id,
              key: field.key ?? '',
              value,
            });

            if (field.detailFieldKey) {
              templateFieldKeys.add(field.detailFieldKey);
              const detailField = currentFields.find((f) => f.key === field.detailFieldKey);
              const detailValue = currentData[field.detailFieldKey];

              if (detailValue) {
                matchingDefects.push({
                  id: detailField?.id,
                  key: field.detailFieldKey,
                  value: detailValue,
                });
              }
            }
          }
        }
      });

      const newFields = newTemplate.fields;

      // templateFieldData is an object (key-value pairs), not an array
      const updatedData = { ...currentData };
      matchingDefects.forEach((defect) => {
        const newField = newFields.find((field) => field?.key === defect.key);
        if (newField && newField.key) {
          updatedData[newField.key] = defect.value;
        }
      });
      return updatedData;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editPointOfInterest.template?.fields, editPointOfInterest.pointOfInterest?.templateFieldData]
  );

  return {
    defectLegendKeys,
    setPoiDefectData,
    getDefectLegendOctreeKeys,
    switchTemplate,
  };
};
